import { Fragment } from 'react';
import { alpha, Box, ButtonBase, Typography, useTheme } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { useWorkspace } from '../store/WorkspaceContext';
import { ROOT_FOLDER_ID } from '../utils/constants';

export default function BreadcrumbNav({ filterCrumb = null, onFilterHomeClick, sx = {} }) {
  const theme = useTheme();
  const { breadcrumbs, currentFolderId, setCurrentFolderId } = useWorkspace();
  const rootCrumb = breadcrumbs[0] || { id: ROOT_FOLDER_ID, name: 'TextLab', parentId: null };
  const pathItems = filterCrumb
    ? [
        { ...rootCrumb, active: false, label: 'Home', isRoot: true },
        { id: 'filter', active: true, label: filterCrumb.label, icon: filterCrumb.icon },
      ]
    : breadcrumbs.map((folder) => ({
        ...folder,
        active: folder.id === currentFolderId,
        label: folder.parentId ? folder.name : 'Home',
        isRoot: !folder.parentId,
      }));

  return (
    <Box
      component="nav"
      aria-label="Folder path"
      sx={{
        display: 'flex',
        maxWidth: '100%',
        minWidth: 0,
        flexGrow: 1,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          maxWidth: '100%',
          minWidth: 0,
          width: '100%',
          height: 42,
          overflowX: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          px: 0.5,
          py: 0.35,
          boxShadow:
            theme.palette.mode === 'dark'
              ? `inset 0 1px 0 ${alpha('#ffffff', 0.04)}`
              : `inset 0 1px 0 ${alpha('#111827', 0.04)}`,
          userSelect: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
        }}
      >
        {pathItems.map((folder, index) => {
          const active = folder.active;
          const isRoot = folder.isRoot;
          const segmentSx = {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            minWidth: 0,
            height: 32,
            px: 1,
            borderRadius: 0.75,
            color: active ? 'primary.main' : 'text.secondary',
            bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
            whiteSpace: 'nowrap',
          };

          return (
            <Fragment key={folder.id}>
              {index > 0 ? (
                <ChevronRightRoundedIcon
                  aria-hidden="true"
                  sx={{ color: 'text.disabled', fontSize: 18, flexShrink: 0 }}
                />
              ) : null}
              {active ? (
                <Box sx={segmentSx}>
                  {isRoot ? <HomeRoundedIcon sx={{ fontSize: 17, flexShrink: 0 }} /> : folder.icon}
                  <Typography variant="body2" fontWeight={800} noWrap>
                    {folder.label}
                  </Typography>
                </Box>
              ) : (
                <ButtonBase
                  onClick={() => {
                    if (filterCrumb && isRoot) {
                      onFilterHomeClick?.();
                    } else {
                      setCurrentFolderId(folder.id);
                    }
                  }}
                  sx={{
                    ...segmentSx,
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'text.primary',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {isRoot ? <HomeRoundedIcon sx={{ fontSize: 17, flexShrink: 0 }} /> : null}
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {folder.label}
                  </Typography>
                </ButtonBase>
              )}
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
